import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { OpportunityWithRelations } from "@/hooks/use-opportunities";
import { formatDate } from "@/lib/format";
import { STAGE_LABELS, opportunityPartyName } from "@/lib/opportunity-calc";
import { cn } from "@/lib/utils";
import { TONE_PILL } from "@/lib/widget-status";

interface ClosedOpportunitiesTableProps {
  rows: OpportunityWithRelations[];
  isLoading: boolean;
  onOpen: (opportunityId: string) => void;
}

/** Vunnet (med lenke til prosjektet) og tapt (med årsak) de siste 90 dagene. */
export default function ClosedOpportunitiesTable({ rows, isLoading, onOpen }: ClosedOpportunitiesTableProps) {
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  return (
    <section aria-label="Avsluttede prosesser" className="space-y-2">
      <h3 className="font-display text-base font-semibold">Avsluttet siste 90 dager ({rows.length})</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ingen vunnede eller tapte prosesser de siste 90 dagene.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kunde</TableHead>
              <TableHead>Prosess</TableHead>
              <TableHead>Eier</TableHead>
              <TableHead>Utfall</TableHead>
              <TableHead>Dato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id}>
                <TableCell>{opportunityPartyName(o)}</TableCell>
                <TableCell>
                  <button type="button" className="text-left underline-offset-4 hover:underline" onClick={() => onOpen(o.id)}>
                    {o.title}
                  </button>
                </TableCell>
                <TableCell>{o.owner?.full_name ?? "Ukjent"}</TableCell>
                <TableCell>
                  <span className={cn("rounded-pill px-2 py-0.5 text-xs font-medium", TONE_PILL[o.stage === "won" ? "reached" : "miss"])}>
                    {STAGE_LABELS[o.stage]}
                  </span>{" "}
                  {o.stage === "won" && o.project_id && (
                    <Link to={`/prosjekter/${o.project_id}`} className="text-sm text-primary hover:underline">
                      Åpne prosjekt
                    </Link>
                  )}
                  {o.stage === "lost" && o.lost_reason && <span className="text-sm text-muted-foreground">{o.lost_reason}</span>}
                </TableCell>
                <TableCell>{formatDate(o.stage === "won" ? o.won_at : o.lost_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
